/**
 * Layout and hit-testing for the in-VR menu panel.
 *
 * Deliberately pure: no three.js, no canvas, no DOM. The panel is drawn to a canvas
 * texture and pointed at with a controller ray, and both of those need to agree exactly
 * on where every button is. Deriving the geometry once, here, means the drawing code and
 * the hit-testing code cannot drift apart — and it makes the fiddly parts (which rows are
 * visible, where the scroll limits are, and the texture-to-canvas coordinate flip)
 * testable without a headset.
 *
 * All coordinates are in canvas pixels with the origin at the top left.
 */

export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export type MenuAction =
  /** Put this model in the room, or take it out again if it is already there. */
  | { readonly type: 'toggle'; readonly id: string }
  | { readonly type: 'delete'; readonly id: string }
  | { readonly type: 'scroll'; readonly delta: number }
  | { readonly type: 'exit' };

export interface MenuModel {
  readonly id: string;
  readonly name: string;
  readonly detail: string;
  /** Whether this model is currently standing in the room. */
  readonly inRoom: boolean;
  /**
   * How large it is being shown, as a percentage, or absent when that is its true size.
   *
   * Absent for the ordinary case rather than a row of "100%" against every model: the
   * figure is worth reading precisely because it is unusual, and one shown against
   * everything is one nobody reads. A row that carries it is a row saying this model is
   * not the size its file claims.
   */
  readonly scaleLabel?: string;
}

export interface MenuRegion {
  /** Stable identity, so hover changes can be detected without deep comparison. */
  readonly key: string;
  readonly action: MenuAction;
  readonly rect: Rect;
  readonly label: string;
  readonly enabled: boolean;
}

export interface MenuRow {
  readonly model: MenuModel;
  /** The row itself, which puts the model in the room or takes it out. */
  readonly toggleRect: Rect;
  readonly deleteRect: Rect;
}

export interface MenuLayout {
  readonly rows: readonly MenuRow[];
  readonly regions: readonly MenuRegion[];
  readonly scrollOffset: number;
  readonly totalCount: number;
  readonly canScrollUp: boolean;
  readonly canScrollDown: boolean;
  /** Human-readable position within the list, or undefined when it all fits. */
  readonly rangeLabel: string | undefined;
}

export const PANEL_WIDTH_PX = 1024;
export const PANEL_HEIGHT_PX = 768;

/**
 * Whether an account is signed in, as shown in the panel's header.
 *
 * The panel reports the state and offers nothing to act on: a virtual keyboard is a
 * miserable way to type an email address and a password, so signing in belongs to the
 * desktop browser. What this answers is the question a headset user can actually be stuck
 * on — "am I signed in on this thing?" — which is otherwise unanswerable without taking
 * the headset off.
 *
 * The wording lives here, next to the rest of the panel's text, rather than in the
 * painting code, so it can be tested without a canvas.
 */
export function accountStatusLabel(email: string | undefined): string {
  return email === undefined ? 'Not signed in' : `Signed in as ${email}`;
}

/**
 * The line under the title, saying what the list is for and what is already out.
 *
 * A count rather than a list of names: with a roomful, what a user needs from the panel
 * is confirmation that the thing they just tapped went somewhere, and they can see the
 * models themselves by looking up.
 */
export function roomStatusLabel(inRoom: number, libraryCount: number): string {
  if (libraryCount === 0)
    return 'Nothing stored yet. Add STL files from the desktop browser.';
  if (inRoom === 0) return 'Tap a model to put it in the room.';
  if (inRoom === 1) return '1 model in the room. Tap to add or remove.';
  return `${inRoom.toString()} models in the room. Tap to add or remove.`;
}

/** Physical size of the panel in metres. Roughly a large monitor at arm's length. */
export const PANEL_WIDTH_M = 0.62;
export const PANEL_HEIGHT_M = (PANEL_WIDTH_M * PANEL_HEIGHT_PX) / PANEL_WIDTH_PX;

const PADDING = 32;
const LIST_TOP = 150;
const LIST_BOTTOM = 620;
const ROW_HEIGHT = 72;
/** Tuned so a whole extra row fits in the list area rather than sitting empty. */
const ROW_GAP = 6;
const DELETE_WIDTH = 84;
const FOOTER_TOP = 648;
const FOOTER_HEIGHT = 80;
const SCROLL_WIDTH = 80;
const EXIT_WIDTH = 280;

export const VISIBLE_ROWS = Math.floor(
  (LIST_BOTTOM - LIST_TOP + ROW_GAP) / (ROW_HEIGHT + ROW_GAP),
);

export const LIST_AREA: Rect = {
  x: PADDING,
  y: LIST_TOP,
  width: PANEL_WIDTH_PX - PADDING * 2,
  height: LIST_BOTTOM - LIST_TOP,
};

/**
 * Clamp a scroll offset so the list can never be scrolled past its own ends.
 *
 * Deleting the last few models can leave an offset pointing beyond the list, so this is
 * applied on every layout rather than only when scrolling.
 */
export function clampScroll(offset: number, totalCount: number): number {
  const maxOffset = Math.max(0, totalCount - VISIBLE_ROWS);
  if (!Number.isFinite(offset)) return 0;
  return Math.min(Math.max(Math.trunc(offset), 0), maxOffset);
}

export function layoutMenu(models: readonly MenuModel[], scrollOffset = 0): MenuLayout {
  const totalCount = models.length;
  const offset = clampScroll(scrollOffset, totalCount);
  const visible = models.slice(offset, offset + VISIBLE_ROWS);

  const rows: MenuRow[] = visible.map((model, index) => {
    const y = LIST_TOP + index * (ROW_HEIGHT + ROW_GAP);
    return {
      model,
      toggleRect: {
        x: LIST_AREA.x,
        y,
        width: LIST_AREA.width - DELETE_WIDTH,
        height: ROW_HEIGHT,
      },
      deleteRect: {
        x: LIST_AREA.x + LIST_AREA.width - DELETE_WIDTH,
        y,
        width: DELETE_WIDTH,
        height: ROW_HEIGHT,
      },
    };
  });

  const canScrollUp = offset > 0;
  const canScrollDown = offset + VISIBLE_ROWS < totalCount;

  const regions: MenuRegion[] = [];

  for (const row of rows) {
    regions.push({
      key: `toggle:${row.model.id}`,
      action: { type: 'toggle', id: row.model.id },
      rect: row.toggleRect,
      // Says what pressing will do rather than naming the row, since for a control that
      // toggles those are different things and only one of them is any use to act on.
      label: row.model.inRoom
        ? `Take ${row.model.name} out of the room`
        : `Put ${row.model.name} in the room`,
      enabled: true,
    });
    regions.push({
      key: `delete:${row.model.id}`,
      action: { type: 'delete', id: row.model.id },
      rect: row.deleteRect,
      label: `Delete ${row.model.name}`,
      enabled: true,
    });
  }

  regions.push({
    key: 'scroll:up',
    action: { type: 'scroll', delta: -1 },
    rect: { x: PADDING, y: FOOTER_TOP, width: SCROLL_WIDTH, height: FOOTER_HEIGHT },
    label: 'Scroll up',
    enabled: canScrollUp,
  });
  regions.push({
    key: 'scroll:down',
    action: { type: 'scroll', delta: 1 },
    rect: {
      x: PADDING + SCROLL_WIDTH + ROW_GAP,
      y: FOOTER_TOP,
      width: SCROLL_WIDTH,
      height: FOOTER_HEIGHT,
    },
    label: 'Scroll down',
    enabled: canScrollDown,
  });
  regions.push({
    key: 'exit',
    action: { type: 'exit' },
    rect: {
      x: PANEL_WIDTH_PX - PADDING - EXIT_WIDTH,
      y: FOOTER_TOP,
      width: EXIT_WIDTH,
      height: FOOTER_HEIGHT,
    },
    label: 'Leave VR',
    enabled: true,
  });

  return {
    rows,
    regions,
    scrollOffset: offset,
    totalCount,
    canScrollUp,
    canScrollDown,
    rangeLabel:
      totalCount > VISIBLE_ROWS
        ? `${(offset + 1).toString()}–${(offset + rows.length).toString()} of ${totalCount.toString()}`
        : undefined,
  };
}

/**
 * Find the region under a texture coordinate, as produced by a raycast against the panel.
 *
 * Texture coordinates run bottom-to-top while the canvas runs top-to-bottom, so `v` is
 * flipped here. Getting this backwards produces a menu that responds to clicks mirrored
 * about its middle, which is exactly the sort of thing that is obvious in a headset and
 * invisible in code review.
 */
export function regionAt(
  layout: MenuLayout,
  u: number,
  v: number,
): MenuRegion | undefined {
  const x = u * PANEL_WIDTH_PX;
  const y = (1 - v) * PANEL_HEIGHT_PX;

  return layout.regions.find((region) => region.enabled && contains(region.rect, x, y));
}

function contains(rect: Rect, x: number, y: number): boolean {
  return (
    x >= rect.x && x <= rect.x + rect.width && y >= rect.y && y <= rect.y + rect.height
  );
}
